/**
 * ZONIY - Layout Component
 * @module Layout
 * @author Osman - Frontend Developer
 * @description Shared layout with navigation
 * @version 1.0.0
 * Git: Osman <osman@linesoft.com> - Layout component
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Search, LogOut, Package, Heart, FileText, Settings, Users, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
                <span className="font-heading font-black text-xl">Z</span>
              </div>
              <span className="font-heading font-bold text-xl tracking-tight hidden sm:block">ZONIY</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link 
                to="/search" 
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                data-testid="nav-search"
              >
                <Search className="w-4 h-4" />
                Search Parts
              </Link>
              {user?.role === 'vendor' && (
                <Link 
                  to="/vendor" 
                  className="text-slate-300 hover:text-white transition-colors"
                  data-testid="nav-vendor"
                >
                  Vendor Portal
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link 
                  to="/admin" 
                  className="text-slate-300 hover:text-white transition-colors"
                  data-testid="nav-admin"
                >
                  Admin Portal
                </Link>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <Link to="/cart" className="relative" data-testid="cart-link">
                    <ShoppingCart className="w-6 h-6" />
                    {itemCount > 0 && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-orange-500 rounded-full text-xs flex items-center justify-center font-bold">
                        {itemCount}
                      </span>
                    )}
                  </Link>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="user-menu">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4" />
                        </div>
                        <span className="hidden sm:block">{user.name}</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-1.5">
                        <p className="font-medium">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/orders" className="flex items-center gap-2 cursor-pointer">
                          <Package className="w-4 h-4" />
                          My Orders
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/favorites" className="flex items-center gap-2 cursor-pointer">
                          <Heart className="w-4 h-4" />
                          Favorites
                        </Link>
                      </DropdownMenuItem>
                      {user.role === 'vendor' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/vendor" className="flex items-center gap-2 cursor-pointer">
                              <BarChart3 className="w-4 h-4" />
                              Vendor Dashboard
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/vendor/inventory" className="flex items-center gap-2 cursor-pointer">
                              <Package className="w-4 h-4" />
                              Inventory
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      {user.role === 'admin' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem asChild>
                            <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                              <BarChart3 className="w-4 h-4" />
                              Admin Dashboard
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to="/admin/vendors" className="flex items-center gap-2 cursor-pointer">
                              <Users className="w-4 h-4" />
                              Manage Vendors
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={handleLogout}
                        className="flex items-center gap-2 cursor-pointer text-red-600"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <div className="hidden md:flex items-center gap-3">
                  <Link to="/login" data-testid="login-link">
                    <Button variant="ghost" className="text-white hover:text-blue-400">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" data-testid="register-link">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      Register
                    </Button>
                  </Link>
                </div>
              )}
              
              {/* Mobile menu button */}
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-800 px-4 py-4">
            <div className="flex flex-col gap-4">
              <Link 
                to="/search" 
                className="text-slate-300 hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                Search Parts
              </Link>
              {user?.role === 'vendor' && (
                <Link 
                  to="/vendor" 
                  className="text-slate-300 hover:text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Vendor Portal
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link 
                  to="/admin" 
                  className="text-slate-300 hover:text-white"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Admin Portal
                </Link>
              )}
              {user ? (
                <>
                  <Link 
                    to="/orders" 
                    className="text-slate-300 hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Orders
                  </Link>
                  <Link 
                    to="/favorites" 
                    className="text-slate-300 hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Favorites
                  </Link>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="text-red-400 hover:text-red-300 text-left"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="text-slate-300 hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="text-slate-300 hover:text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 flex items-center justify-center">
                <span className="font-heading font-black text-sm text-white">Z</span>
              </div>
              <span className="font-heading font-bold text-white">ZONIY</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/search" className="hover:text-white">Search</Link>
              <Link to="/register" className="hover:text-white">Become a Vendor</Link>
            </div>
            <div className="text-slate-500 text-sm">
              © 2024 Zoniy. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
